import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { CtaButton } from './CtaButton';
import { gallery } from '../data/content';
const ease = [0.23, 1, 0.32, 1] as const;
const arrowClass = 'absolute top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-blue-800 shadow-lg transition-colors duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-300 md:h-12 md:w-12';
export function Gallery() {
  const [index, setIndex] = useState(0);
  const total = gallery.length;
  const current = gallery[index];
  const goTo = (next: number) => setIndex((next + total) % total);
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goTo(index - 1);
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goTo(index + 1);
    }
  };
  return <section className="w-full bg-blue-50 py-14 text-slate-900 md:py-20" data-id="element-63" data-mp="rcb07nn17r6n">
      <div className="mx-auto w-full max-w-5xl px-5 md:px-8" data-id="element-64" data-mp="wactf1e696vu">
        <header className="mx-auto max-w-4xl text-center" data-id="element-65" data-mp="112mgm9nwjsuf">
          <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-blue-600" data-id="element-66" data-mp="1135w7jq2y55a">
            Mirá las preguntas por dentro
          </p>
          <h2 className="mt-4 text-[2rem] font-extrabold leading-[1.1] tracking-tight md:text-5xl" data-id="element-67" data-mp="8e8fhl1iogen">
            Así se ve una ronda de práctica
          </h2>
        </header>

        <div role="group" aria-roledescription="carrusel" aria-label="Galería de mapas visuales" tabIndex={0} onKeyDown={handleKeyDown} className="relative mt-12 rounded-2xl focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-400" data-id="element-68" data-mp="hedbkz19fsdi">
          <div className="overflow-hidden rounded-2xl" data-id="element-69" data-mp="1xs3sj6dhab4k">
            <motion.div className="flex cursor-grab active:cursor-grabbing" animate={{
            x: `-${index * 100}%`
          }} transition={{
            duration: 0.3,
            ease
          }} drag="x" dragConstraints={{
            left: 0,
            right: 0
          }} dragElastic={0.12} onDragEnd={(_, info) => {
            if (info.offset.x < -60) goTo(index + 1);
            if (info.offset.x > 60) goTo(index - 1);
          }} data-id="element-70" data-mp="1xuq0360dcqms">
              {gallery.map((item, itemIndex) => <figure key={item.title} className="w-full shrink-0" aria-hidden={itemIndex !== index} data-id="element-71" data-mp="366pbo6hjgemi" data-mpi={itemIndex}>
                  <img src={item.image} alt={`Mapa visual de ${item.title}: electrodos, canales y referencias anatómicas`} className="pointer-events-none w-full rounded-2xl bg-white ring-1 ring-blue-100" draggable={false} loading={itemIndex === 0 ? 'eager' : 'lazy'} data-id="element-72" data-mp="3kvljc2fonpml" />
                </figure>)}
            </motion.div>
          </div>

          <button type="button" onClick={() => goTo(index - 1)} aria-label="Mapa anterior" className={`${arrowClass} left-2 md:-left-5`} data-id="element-73" data-mp="12vu1l1zwmtjn">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-74" data-mp="tv5n666lmr68" />
          </button>
          <button type="button" onClick={() => goTo(index + 1)} aria-label="Mapa siguiente" className={`${arrowClass} right-2 md:-right-5`} data-id="element-75" data-mp="xwhi8r88s4lt">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-76" data-mp="1ql56mvoafa3l" />
          </button>
        </div>

        <div className="mx-auto mt-7 max-w-4xl text-center" aria-live="polite" data-id="element-77" data-mp="1cpkrmbbojc1g">
          <p className="text-xl font-extrabold tracking-tight md:text-2xl" data-id="element-78" data-mp="1hkq8gvq860io">{current.title}</p>
          <p className="mt-2 text-slate-600" data-id="element-79" data-mp="16qd7y2bqz1r">{current.caption}</p>
        </div>

        <div className="mt-7 flex items-center justify-center gap-3" data-id="element-80" data-mp="68fmb85zl2n7">
          {gallery.map((item, itemIndex) => <button key={item.title} type="button" onClick={() => goTo(itemIndex)} aria-label={`Ver ${item.title}`} aria-current={itemIndex === index} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-400 ${itemIndex === index ? 'w-8 bg-blue-600' : 'w-2.5 bg-blue-200 hover:bg-blue-300'}`} data-id="element-81" data-mp="lim38jd1unqx" data-mpi={itemIndex} />)}
        </div>

        <p className="mt-6 text-center text-sm text-slate-500" data-id="element-82" data-mp="vdw6faf6p16a">
          Deslizá o usá las flechas — {index + 1} de {total} muestras reales del material
        </p>

        <div className="mt-8 flex flex-col items-center text-center" data-id="element-83" data-mp="3g56bx1z2zlrk">
          <CtaButton variant="secondary" size="md" data-id="element-84" data-mp="3g33evxzigsdc">
            Quiero acceder a las +300 Preguntas
          </CtaButton>
          <p className="mt-3 text-sm text-slate-600" data-id="element-85" data-mp="1qokln6eodzbc">
            Acceso inmediato · PDF imprimible · Respuestas comentadas incluidas
          </p>
        </div>
      </div>
    </section>;
}